import React from 'react';
import Table from 'react-bootstrap/Table';

const TotalsMiniTable = ({
  totalsData,
  getBookLogoUrl,
  getCellStyle,
  getTooltipText,
  onOddsClick,
  game,
  periodsToShow = null
}) => {
  const safeGame = { away: 'TBD', home: 'TBD', gameIDmlb: 'loading', ...game };

  if (!totalsData || totalsData.length === 0) {
    return null;
  }

  // Group by period
  const groupedByPeriod = totalsData.reduce((acc, player) => {
    const period = player.period || 'Game';
    if (!acc[period]) {
      acc[period] = [];
    }
    acc[period].push(player);
    return acc;
  }, {});

  // Define period order
  const periodOrder = ['Game', 'Q1', 'Q2', 'Q3', 'Q4', '1st Half', '2nd Half', '1st', '2nd', '3rd'];

  // Filter periods based on periodsToShow prop
  let periodsToDisplay = Object.keys(groupedByPeriod);
  if (periodsToShow && Array.isArray(periodsToShow)) {
    periodsToDisplay = periodsToDisplay.filter(period => periodsToShow.includes(period));
  }

  const sortedPeriods = periodsToDisplay.sort((a, b) => {
    const indexA = periodOrder.indexOf(a);
    const indexB = periodOrder.indexOf(b);
    return (indexA === -1 ? 999 : indexA) - (indexB === -1 ? 999 : indexB);
  });

  // Function to find best odds
  const findBestOdds = (player) => {
    const books = ['fanduel', 'draftkings', 'betmgm', 'caesars', 'bet365', 'pointsbet', 'betrivers', 'hardrockbet', 'bovada', 'fanatics', 'espnbet'];
    let bestOdds = null;
    let bestBook = null;
    let bookCount = 0;

    books.forEach(book => {
      const odds = player[`totals_${book}`];
      if (odds !== null && odds !== undefined) {
        bookCount++;
        if (bestOdds === null || odds > bestOdds) {
          bestOdds = odds;
          bestBook = book;
        }
      }
    });

    const bestBookPrice = player.totals_bestBookPrice;
    const bestBookDiff = player.totals_bestBookDiff;

    return { bestOdds, bestBook, bestBookPrice, bestBookDiff, bookCount };
  };

  return (
    <div className="table-container" style={{ width: '50%' }}>
      <Table bordered size="sm" className="text-center">
        <tbody>
          {sortedPeriods.map((period) => {
            // Group by over/under for this period
            const periodData = groupedByPeriod[period];
            const overData = periodData.filter(p => p.name?.toLowerCase() === 'over');
            const underData = periodData.filter(p => p.name?.toLowerCase() === 'under');

            // Find lowest over line
            const lowestOver = overData.reduce((min, curr) =>
              !min || curr.point < min.point ? curr : min
            , null);

            // Find highest under line
            const highestUnder = underData.reduce((max, curr) =>
              !max || curr.point > max.point ? curr : max
            , null);

            const overBest = lowestOver ? findBestOdds(lowestOver) : null;
            const underBest = highestUnder ? findBestOdds(highestUnder) : null;

            return (
              <React.Fragment key={period}>
                {/* Period section header */}
                <tr>
                  <td colSpan={2} style={{
                    backgroundColor: 'transparent',
                    color: '#ffc107',
                    fontWeight: 'bold',
                    textAlign: 'center',
                    padding: '8px',
                    borderTop: 'none',
                    borderBottom: 'none',
                    height: '40px'
                  }}>
                    {period === 'Game' ? 'Totals' : `Totals ${period}`}
                  </td>
                </tr>
                {/* Over row */}
                {lowestOver && overBest && (
                  <tr>
                    <td style={{ fontWeight: 'bold' }}>O{lowestOver.point}</td>
                    <td
                      style={{
                        ...getCellStyle(overBest.bestOdds, overBest.bestBookPrice, overBest.bestBookDiff, overBest.bookCount),
                        cursor: overBest.bestOdds ? 'pointer' : 'default'
                      }}
                      title={getTooltipText(overBest.bestOdds, overBest.bestBookPrice, overBest.bestBookDiff, overBest.bookCount)}
                      onClick={() => {
                        if (overBest.bestOdds && overBest.bestBook && onOddsClick) {
                          onOddsClick(safeGame.gameIDmlb, lowestOver.playerId, 'Over', 'totals', overBest.bestBook, overBest.bestOdds, safeGame.away, safeGame.home);
                        }
                      }}
                    >
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                        {overBest.bestBook && (
                          <img
                            src={getBookLogoUrl(overBest.bestBook)}
                            alt={overBest.bestBook}
                            style={{ height: '16px', width: 'auto' }}
                          />
                        )}
                        <span style={{ fontSize: '0.9rem', fontWeight: 'bold' }}>
                          {overBest.bestOdds > 0 ? `+${overBest.bestOdds}` : overBest.bestOdds}
                        </span>
                      </div>
                    </td>
                  </tr>
                )}
                {/* Under row */}
                {highestUnder && underBest && (
                  <tr>
                    <td style={{ fontWeight: 'bold' }}>U{highestUnder.point}</td>
                    <td
                      style={{
                        ...getCellStyle(underBest.bestOdds, underBest.bestBookPrice, underBest.bestBookDiff, underBest.bookCount),
                        cursor: underBest.bestOdds ? 'pointer' : 'default'
                      }}
                      title={getTooltipText(underBest.bestOdds, underBest.bestBookPrice, underBest.bestBookDiff, underBest.bookCount)}
                      onClick={() => {
                        if (underBest.bestOdds && underBest.bestBook && onOddsClick) {
                          onOddsClick(safeGame.gameIDmlb, highestUnder.playerId, 'Under', 'totals', underBest.bestBook, underBest.bestOdds, safeGame.away, safeGame.home);
                        }
                      }}
                    >
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                        {underBest.bestBook && (
                          <img
                            src={getBookLogoUrl(underBest.bestBook)}
                            alt={underBest.bestBook}
                            style={{ height: '16px', width: 'auto' }}
                          />
                        )}
                        <span style={{ fontSize: '0.9rem', fontWeight: 'bold' }}>
                          {underBest.bestOdds > 0 ? `+${underBest.bestOdds}` : underBest.bestOdds}
                        </span>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </Table>
    </div>
  );
};

export default TotalsMiniTable;
